// Net for code-split apps (Vite lazy routes) served from a host (PWA / Vercel).
//
// When a new build deploys, chunk filenames change (content hash). A tab or PWA
// opened *before* the deploy still runs the old entry, which references chunk
// URLs that now 404. The next lazy import() then fails → blank screen until a
// manual hard refresh. PWAs are hit hardest because sessions stay open for days,
// and our SW uses skipWaiting + clients.claim (immediate takeover), which can
// swap chunks mid-session.
//
// Fix: on a dynamic-import failure, silently reload once to fetch the fresh
// entry. Guarded by sessionStorage so we never loop. See the mobile-hybrid-audit
// skill, references/hybrid-architecture.md §2.

const CHUNK_ERR =
  /Loading chunk|dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i;

export function isChunkLoadError(err) {
  const msg = String(err?.message ?? err?.reason ?? err ?? '');
  return CHUNK_ERR.test(msg);
}

export function reloadOnce() {
  try {
    if (sessionStorage.getItem('chunk-reloaded')) return;
    sessionStorage.setItem('chunk-reloaded', '1');
  } catch { /* sessionStorage unavailable — reload anyway */ }
  window.location.reload();
}

export function installChunkReloadGuard() {
  // Vite dispatches this when a dynamically-imported module fails to preload.
  window.addEventListener('vite:preloadError', reloadOnce);
  window.addEventListener('error', (e) => { if (isChunkLoadError(e)) reloadOnce(); });
  window.addEventListener('unhandledrejection', (e) => { if (isChunkLoadError(e)) reloadOnce(); });
  // A clean load means the new entry took — clear the guard for next time.
  window.addEventListener('load', () => {
    try { sessionStorage.removeItem('chunk-reloaded'); } catch { /* ignore */ }
  });
}
